// @seventide/types — attorney module DTOs (FR-8.1–FR-8.5, R-5.4).
// Owned by the attorney module agent (see docs/ARCHITECTURE.md §6).
import { z } from 'zod';
import { Id, IsoDate, IsoDateTime, PaginationQuery, Pence, paginated } from './common';
import { AccountType, AttorneyActing, LegalInstrumentType, TransactionCategory } from './enums';

// ---------------------------------------------------------------- shared enums

/** Decision status (FR-8.2, FR-8.5). `signed` = complete (all required signatures, or recorded by one attorney). */
export const DECISION_STATUSES = ['draft', 'awaiting_signatures', 'signed'] as const;
export const DecisionStatus = z.enum(DECISION_STATUSES);
export type DecisionStatus = z.infer<typeof DecisionStatus>;
export const DECISION_STATUS_LABELS: Record<DecisionStatus, string> = {
  draft: 'Draft',
  awaiting_signatures: 'Awaiting co-signature',
  signed: 'Recorded',
};

/** How the attorneys were appointed (from the legal instrument) — `none` when no instrument is recorded. */
export const ACTING_BASES = ['jointly', 'jointly_and_severally', 'sole', 'none'] as const;
export const ActingBasis = z.enum(ACTING_BASES);
export type ActingBasis = z.infer<typeof ActingBasis>;
export const ACTING_BASIS_LABELS: Record<ActingBasis, string> = {
  jointly: 'Jointly (attorneys must act together)',
  jointly_and_severally: 'Jointly and severally (any attorney may act alone)',
  sole: 'Sole attorney',
  none: 'No power of attorney recorded',
};

/** FR-8.3 expense types. */
export const ATTORNEY_EXPENSE_CATEGORIES = ['travel', 'postage', 'phone', 'professional', 'other'] as const;
export const AttorneyExpenseCategory = z.enum(ATTORNEY_EXPENSE_CATEGORIES);
export type AttorneyExpenseCategory = z.infer<typeof AttorneyExpenseCategory>;
export const ATTORNEY_EXPENSE_CATEGORY_LABELS: Record<AttorneyExpenseCategory, string> = {
  travel: 'Travel and parking',
  postage: 'Postage',
  phone: 'Phone calls',
  professional: 'Professional fees',
  other: 'Other',
};

/** Wording the attorney agrees to when signing a decision (FR-8.5). */
export const DECISION_SIGNATURE_STATEMENT =
  'I confirm that this is a true record of the decision and that I agree it was made in the best interests of the person.';

/** Turns "  sarah  HUGHES " into "sarah hughes" for comparing a typed signature with a name. */
export function normaliseSignatureName(s: string): string {
  return s.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
}

// ---------------------------------------------------------------- decisions (FR-8.2)

const text = (max: number, msg = 'This is too long') => z.string().max(max, msg);

export const DecisionOption = z.object({
  text: z.string().trim().min(1, 'Describe the option').max(500, 'Keep each option under 500 characters'),
  /** The option that was chosen. */
  chosen: z.boolean().default(false),
});
export type DecisionOption = z.infer<typeof DecisionOption>;

export const ConsultedPerson = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(120, 'Keep the name under 120 characters'),
  /** Relationship or role, e.g. "Daughter, attorney" or "GP". */
  role: text(120).default(''),
  /** What they said (optional). */
  views: text(2000).optional(),
});
export type ConsultedPerson = z.infer<typeof ConsultedPerson>;

/** One Mental Capacity Act best-interests checklist prompt and the attorney's answer. */
export const BestInterestsAnswer = z.object({
  prompt: z.string().trim().min(1).max(500),
  answer: text(4000).default(''),
});
export type BestInterestsAnswer = z.infer<typeof BestInterestsAnswer>;

/** Fields the user fills in (shared by create and update). */
export const DecisionFields = z.object({
  date: IsoDate,
  title: z.string().trim().min(1, 'Give the decision a short title').max(200, 'Keep the title under 200 characters'),
  /** Content template id (getDecisionTemplates()), or null for a custom decision. */
  templateId: z.string().max(80).nullable().default(null),
  /** What is being decided. */
  decision: z.string().trim().min(1, 'Describe what was decided').max(4000, 'Keep this under 4,000 characters'),
  options: z.array(DecisionOption).max(20, 'Add up to 20 options').default([]),
  /** The person's known wishes and feelings (E). */
  wishes: text(8000).default(''),
  consulted: z.array(ConsultedPerson).max(30, 'Add up to 30 people').default([]),
  /** MCA checklist answers (E). */
  bestInterests: z.array(BestInterestsAnswer).max(20).default([]),
  /** Best-interests reasoning (E). */
  reasoning: text(8000).default(''),
  outcome: text(4000).default(''),
  reviewDate: IsoDate.nullable().default(null),
});
export type DecisionFields = z.infer<typeof DecisionFields>;
export type DecisionFieldsInput = z.input<typeof DecisionFields>;

export const SignDecisionRequest = z.object({
  /** The attorney types their full name to sign. */
  typedName: z.string().trim().min(2, 'Type your full name to sign').max(120),
  /** Must be true: the attorney agrees to DECISION_SIGNATURE_STATEMENT. */
  confirm: z.literal(true, { message: 'Tick the box to confirm' }),
});
export type SignDecisionRequest = z.infer<typeof SignDecisionRequest>;

export const CreateDecisionRequest = DecisionFields.extend({
  /** Client-generated id for idempotent offline creates (ADR 0004). */
  id: Id.optional(),
  /** false = save as a draft; true (default) = record it (and start co-signature if needed). */
  finalise: z.boolean().default(true),
  /** Sign at the same time (attorneys only). */
  signature: SignDecisionRequest.optional(),
});
export type CreateDecisionRequest = z.infer<typeof CreateDecisionRequest>;
export type CreateDecisionInput = z.input<typeof CreateDecisionRequest>;

/** Full replacement. Changing a recorded decision removes existing signatures (they agreed to the old text). */
export const UpdateDecisionRequest = DecisionFields.extend({
  finalise: z.boolean().default(true),
  signature: SignDecisionRequest.optional(),
});
export type UpdateDecisionRequest = z.infer<typeof UpdateDecisionRequest>;
export type UpdateDecisionInput = z.input<typeof UpdateDecisionRequest>;

export const AttorneyPersonRef = z.object({ userId: Id, name: z.string() });
export type AttorneyPersonRef = z.infer<typeof AttorneyPersonRef>;

export const DecisionSigner = z.object({
  userId: Id,
  name: z.string(),
  signed: z.boolean(),
  signedAt: IsoDateTime.nullable(),
  /** The name they typed when signing. */
  typedName: z.string().nullable(),
});
export type DecisionSigner = z.infer<typeof DecisionSigner>;

export const DecisionDto = DecisionFields.extend({
  id: Id,
  status: DecisionStatus,
  requiresCoSignature: z.boolean(),
  actingBasis: ActingBasis,
  /** Required signers (jointly) or the attorneys who chose to sign (otherwise), plus their status. */
  signers: z.array(DecisionSigner),
  signaturesCount: z.number().int(),
  requiredSignaturesCount: z.number().int(),
  /** The current user may sign now. */
  canSign: z.boolean(),
  /** The current user has already signed. */
  signedByMe: z.boolean(),
  createdBy: AttorneyPersonRef.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  finalisedAt: IsoDateTime.nullable(),
});
export type DecisionDto = z.infer<typeof DecisionDto>;

export const DecisionListItem = z.object({
  id: Id,
  date: IsoDate,
  title: z.string(),
  templateId: z.string().nullable(),
  status: DecisionStatus,
  requiresCoSignature: z.boolean(),
  actingBasis: ActingBasis,
  signaturesCount: z.number().int(),
  requiredSignaturesCount: z.number().int(),
  awaitingMySignature: z.boolean(),
  reviewDate: IsoDate.nullable(),
  createdBy: AttorneyPersonRef.nullable(),
});
export type DecisionListItem = z.infer<typeof DecisionListItem>;

export const DecisionListQuery = PaginationQuery.extend({
  status: DecisionStatus.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  /** Case-insensitive search in the title (titles are encrypted, so filtering happens after decryption). */
  q: z.string().trim().max(100).optional(),
  /** Only decisions the current user still needs to sign. */
  mine: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type DecisionListQuery = z.infer<typeof DecisionListQuery>;
export const DecisionList = paginated(DecisionListItem);
export type DecisionList = z.infer<typeof DecisionList>;

export const DecisionLogExportRequest = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  /** Leave out drafts (default true). */
  excludeDrafts: z.boolean().default(true),
});
export type DecisionLogExportRequest = z.infer<typeof DecisionLogExportRequest>;

// ---------------------------------------------------------------- attorney context (FR-8.5, R-3.2)

export const InstrumentAttorney = z.object({
  name: z.string(),
  userId: Id.nullable(),
  /** The named attorney is a member of this workspace. */
  isMember: z.boolean(),
});
export type InstrumentAttorney = z.infer<typeof InstrumentAttorney>;

export const AttorneyInstrument = z.object({
  id: Id,
  type: LegalInstrumentType,
  typeLabel: z.string(),
  registrationDate: IsoDate.nullable(),
  attorneys: z.array(InstrumentAttorney),
  acting: AttorneyActing.nullable(),
  opgReference: z.string().nullable(),
  hasDocument: z.boolean(),
});
export type AttorneyInstrument = z.infer<typeof AttorneyInstrument>;

export const AttorneyContext = z.object({
  /** The financial instrument used for attorney records (LPA PFA, EPA or deputyship), if any. */
  instrument: AttorneyInstrument.nullable(),
  actingBasis: ActingBasis,
  /** Decisions need every attorney's signature (FR-8.5: acting jointly). */
  coSignatureRequired: z.boolean(),
  /** Members with the attorney role (plus an owner who is a named attorney). */
  attorneyMembers: z.array(AttorneyPersonRef),
  currentUser: z.object({
    userId: Id,
    name: z.string().nullable(),
    /** May sign decisions and is listed as an attorney (by role or on the instrument). */
    isAttorney: z.boolean(),
  }),
});
export type AttorneyContext = z.infer<typeof AttorneyContext>;

export const AttorneySummary = z.object({
  available: z.boolean(),
  decisionsCount: z.number().int().optional(),
  awaitingSignatures: z.number().int().optional(),
  awaitingMySignature: z.number().int().optional(),
  draftsCount: z.number().int().optional(),
  expensesThisTaxYearPence: Pence.optional(),
  actingBasis: ActingBasis.optional(),
  nextReviewDate: IsoDate.nullable().optional(),
});
export type AttorneySummary = z.infer<typeof AttorneySummary>;

// ---------------------------------------------------------------- expenses (FR-8.3)

export const AttorneyExpenseFields = z.object({
  date: IsoDate,
  category: AttorneyExpenseCategory,
  amountPence: Pence.positive('Enter an amount more than £0').max(10_000_000_00, 'That amount is too large'),
  /** What it was for (E). */
  description: z.string().trim().min(1, 'Say what the expense was for').max(1000, 'Keep this under 1,000 characters'),
  /** The attorney who paid (default: the current user, if an attorney). */
  attorneyUserId: Id.nullable().default(null),
  /** Date the attorney was repaid from the person's money, if they have been. */
  reimbursedOn: IsoDate.nullable().default(null),
  /** The repayment transaction in the person's account, if recorded. */
  transactionId: Id.nullable().default(null),
});
export type AttorneyExpenseFields = z.infer<typeof AttorneyExpenseFields>;
export type AttorneyExpenseFieldsInput = z.input<typeof AttorneyExpenseFields>;

export const CreateAttorneyExpenseRequest = AttorneyExpenseFields.extend({ id: Id.optional() });
export type CreateAttorneyExpenseRequest = z.infer<typeof CreateAttorneyExpenseRequest>;
export const UpdateAttorneyExpenseRequest = AttorneyExpenseFields;
export type UpdateAttorneyExpenseRequest = z.infer<typeof UpdateAttorneyExpenseRequest>;

export const AttorneyExpenseDto = z.object({
  id: Id,
  date: IsoDate,
  category: AttorneyExpenseCategory,
  amountPence: Pence,
  description: z.string(),
  attorneyUserId: Id.nullable(),
  attorneyName: z.string().nullable(),
  reimbursedOn: IsoDate.nullable(),
  transactionId: Id.nullable(),
  /** Linked receipt documents (only counted when the viewer can see documents). */
  receiptCount: z.number().int(),
  createdAt: IsoDateTime,
});
export type AttorneyExpenseDto = z.infer<typeof AttorneyExpenseDto>;

export const AttorneyExpenseListQuery = PaginationQuery.extend({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  category: AttorneyExpenseCategory.optional(),
  attorneyUserId: Id.optional(),
});
export type AttorneyExpenseListQuery = z.infer<typeof AttorneyExpenseListQuery>;
export const AttorneyExpenseList = paginated(AttorneyExpenseDto).extend({ totalPence: Pence });
export type AttorneyExpenseList = z.infer<typeof AttorneyExpenseList>;

export const ATTORNEY_EXPENSE_PERIODS = ['month', 'tax_year'] as const;
export const AttorneyExpensePeriod = z.enum(ATTORNEY_EXPENSE_PERIODS);
export type AttorneyExpensePeriod = z.infer<typeof AttorneyExpensePeriod>;

export const AttorneyExpenseSummaryQuery = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  groupBy: AttorneyExpensePeriod.default('month'),
});
export type AttorneyExpenseSummaryQuery = z.infer<typeof AttorneyExpenseSummaryQuery>;

export const AttorneyExpenseTotal = z.object({ totalPence: Pence, count: z.number().int() });
export const AttorneyExpenseSummary = z.object({
  from: IsoDate.nullable(),
  to: IsoDate.nullable(),
  totalPence: Pence,
  count: z.number().int(),
  notReimbursedPence: Pence,
  byCategory: z.array(AttorneyExpenseTotal.extend({ category: AttorneyExpenseCategory, label: z.string() })),
  byAttorney: z.array(AttorneyExpenseTotal.extend({ attorneyUserId: Id.nullable(), name: z.string() })),
  byPeriod: z.array(AttorneyExpenseTotal.extend({ period: z.string(), label: z.string() })),
});
export type AttorneyExpenseSummary = z.infer<typeof AttorneyExpenseSummary>;

// ---------------------------------------------------------------- attorney transactions (FR-8.1)

export const AttorneyTransactionsQuery = z.object({
  from: IsoDate,
  to: IsoDate,
  accountId: Id.optional(),
  /** Only transactions attributed to an attorney (default true). */
  attorneyOnly: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
});
export type AttorneyTransactionsQuery = z.infer<typeof AttorneyTransactionsQuery>;

export const AttorneyTransactionRow = z.object({
  id: Id,
  date: IsoDate,
  direction: z.enum(['in', 'out']),
  amountPence: Pence,
  category: TransactionCategory.nullable(),
  categoryLabel: z.string(),
  counterparty: z.string().nullable(),
  reference: z.string().nullable(),
  madeByAttorney: z.boolean(),
  madeByUserId: Id.nullable(),
  madeByName: z.string().nullable(),
  /** Balance after this transaction, or null when no balance has been recorded for the account. */
  runningBalancePence: Pence.nullable(),
  receiptCount: z.number().int(),
});
export type AttorneyTransactionRow = z.infer<typeof AttorneyTransactionRow>;

export const AttorneyAccountLedger = z.object({
  accountId: Id,
  label: z.string(),
  type: AccountType,
  openingBalancePence: Pence.nullable(),
  closingBalancePence: Pence.nullable(),
  moneyInPence: Pence,
  moneyOutPence: Pence,
  /**
   * Closing balance from recorded balances minus (opening + money in − money out). Non-zero means some
   * transactions are missing; null when it cannot be worked out.
   */
  unexplainedDifferencePence: Pence.nullable(),
  /** Plain-English note about how the balances were worked out. */
  balanceNote: z.string(),
});
export type AttorneyAccountLedger = z.infer<typeof AttorneyAccountLedger>;

export const AttorneyTransactionsResponse = z.object({
  /** false when the viewer cannot see the money module. */
  available: z.boolean(),
  from: IsoDate,
  to: IsoDate,
  accounts: z.array(AttorneyAccountLedger.extend({ rows: z.array(AttorneyTransactionRow) })),
  totals: z.object({
    count: z.number().int(),
    attorneyCount: z.number().int(),
    moneyInPence: Pence,
    moneyOutPence: Pence,
  }),
  attorneys: z.array(AttorneyPersonRef),
});
export type AttorneyTransactionsResponse = z.infer<typeof AttorneyTransactionsResponse>;

// ---------------------------------------------------------------- OPG-ready report (FR-8.4, R-5.4)

export const ReportCategoryTotal = z.object({
  key: z.string(),
  label: z.string(),
  totalPence: Pence,
  count: z.number().int(),
});
export type ReportCategoryTotal = z.infer<typeof ReportCategoryTotal>;

export const ReportGift = z.object({
  id: Id,
  date: IsoDate,
  recipientName: z.string(),
  relationship: z.string().nullable(),
  valuePence: Pence,
  description: z.string().nullable(),
  madeByAttorney: z.boolean(),
  attorneyBasis: z.string().nullable(),
});
export type ReportGift = z.infer<typeof ReportGift>;

export const ReportDecision = z.object({
  id: Id,
  date: IsoDate,
  title: z.string(),
  templateId: z.string().nullable(),
  decision: z.string(),
  options: z.array(DecisionOption),
  wishes: z.string(),
  consulted: z.array(ConsultedPerson),
  bestInterests: z.array(BestInterestsAnswer),
  reasoning: z.string(),
  outcome: z.string(),
  reviewDate: IsoDate.nullable(),
  status: DecisionStatus,
  actingBasis: ActingBasis,
  recordedBy: z.string().nullable(),
  signatures: z.array(z.object({ name: z.string(), typedName: z.string().nullable(), signedAt: IsoDateTime })),
  outstandingSigners: z.array(z.string()),
});
export type ReportDecision = z.infer<typeof ReportDecision>;

export const OpgReport = z.object({
  from: IsoDate,
  to: IsoDate,
  principalName: z.string(),
  instrument: AttorneyInstrument.nullable(),
  /** Plain-English statement of the attorney's name and LPA registration details. */
  statement: z.string(),
  preparedBy: z.object({ name: z.string().nullable(), isNamedAttorney: z.boolean() }),
  accounts: z.object({
    available: z.boolean(),
    note: z.string().nullable(),
    items: z.array(AttorneyAccountLedger),
    openingTotalPence: Pence.nullable(),
    closingTotalPence: Pence.nullable(),
  }),
  income: z.object({ available: z.boolean(), totalPence: Pence, byCategory: z.array(ReportCategoryTotal) }),
  expenditure: z.object({ available: z.boolean(), totalPence: Pence, byCategory: z.array(ReportCategoryTotal) }),
  gifts: z.object({
    available: z.boolean(),
    note: z.string().nullable(),
    items: z.array(ReportGift),
    totalPence: Pence,
  }),
  expenses: z.object({
    items: z.array(AttorneyExpenseDto),
    totalPence: Pence,
    byCategory: z.array(ReportCategoryTotal),
  }),
  decisions: z.object({ items: z.array(ReportDecision) }),
  /** Things the attorney should check before sending (e.g. missing balances, unsigned decisions). */
  warnings: z.array(z.string()),
});
export type OpgReport = z.infer<typeof OpgReport>;

export const OpgReportExportRequest = z
  .object({
    from: IsoDate,
    to: IsoDate,
    includeDecisions: z.boolean().default(true),
    /** Add a full list of transactions as an appendix (default true). */
    includeTransactions: z.boolean().default(true),
  })
  .refine((v) => v.from <= v.to, { message: 'The start date must be on or before the end date', path: ['to'] });
export type OpgReportExportRequest = z.infer<typeof OpgReportExportRequest>;

export const TransactionsCsvRequest = z
  .object({
    from: IsoDate,
    to: IsoDate,
    attorneyOnly: z.boolean().default(false),
  })
  .refine((v) => v.from <= v.to, { message: 'The start date must be on or before the end date', path: ['to'] });
export type TransactionsCsvRequest = z.infer<typeof TransactionsCsvRequest>;
